import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useIntercom } from 'react-use-intercom';
import { useAtomValue, useSetAtom } from 'jotai';

import { cn, HeadphoneIcon } from '@restworld/utils-common';
import { Button as ButtonV2 } from '@restworld/ui-ds-base';

import { activeRestaurantAtom } from './google-address-input-result';
import { activeRestaurantOnboardingStateAtom } from '.';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IEstablishmentNotHORECAProps {}

const Container = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full h-full inline-flex flex-col justify-center items-center gap-10', className)} {...props} />
);
const Section = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('self-stretch flex flex-col justify-start items-start gap-4', className)} {...props} />
);
const Title = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5
    className={cn('self-stretch text-center justify-start text-neutral-700 text-xl font-semibold', className)}
    {...props}
  />
);
const Subtitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5
    className={cn('self-stretch text-center justify-start text-neutral-200 text-base font-medium', className)}
    {...props}
  />
);
const HeaderTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5 className={cn('text-center justify-start text-neutral-700 text-sm font-medium', className)} {...props} />
);
const ButtonTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5 className={cn('text-purple-700 text-base font-semibold', className)} {...props} />
);

const EstablishmentNotHORECA: React.FunctionComponent<IEstablishmentNotHORECAProps> = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const { showMessages } = useIntercom();
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const activeRestaurant = useAtomValue(activeRestaurantAtom);

  const redirectToFirstStep = React.useCallback(() => setActiveUI('select_restaurant_from_google'), [setActiveUI]);
  return (
    <Container>
      <Section>
        <Title>{t('establishment_not_horeca.title')}</Title>
        <Subtitle className="flex flex-col gap-8">
          <Subtitle>{activeRestaurant}</Subtitle>
          <Subtitle>{t('establishment_not_horeca.subtitle')}</Subtitle>
        </Subtitle>
      </Section>
      <Section>
        <HeaderTitle>{t('establishment_not_horeca.actions.primary.header_title')}</HeaderTitle>
        <ButtonV2
          EndIcon={MagnifyingGlassIcon}
          variant="secondary-restworld"
          className="w-full"
          size="md"
          onClick={redirectToFirstStep}
        >
          <ButtonTitle>{t('establishment_not_horeca.actions.primary.button_title')}</ButtonTitle>
        </ButtonV2>
      </Section>
      <Section>
        <HeaderTitle>{t('establishment_not_horeca.actions.secondary.header_title')}</HeaderTitle>
        <ButtonV2
          EndIcon={HeadphoneIcon}
          variant="secondary-restworld"
          className="w-full"
          size="md"
          onClick={(e) => {
            e.preventDefault();
            showMessages();
          }}
        >
          <ButtonTitle>{t('establishment_not_horeca.actions.secondary.button_title')}</ButtonTitle>
        </ButtonV2>
      </Section>
    </Container>
  );
};

export default EstablishmentNotHORECA;
